/* ============================================================
   REMOTE — draggable clicker. Buttons dispatch store actions via
   act(); the LCD reflects the current channel. Drag + position are
   store-owned. In fullscreen the remote docks to the bottom edge:
   it peeks on bottom-edge hover and slides fully in on click, then
   auto-hides after 5s idle (all driven by store state).
   ============================================================ */
import { createPortal } from 'react-dom'
import { useTv } from '@/tv/context'
import { Touchscreen } from './Touchscreen'
import styles from './Remote.module.css'

const pad = (n: number) => (n < 10 ? '0' : '') + n

export function Remote() {
  const tv = useTv()
  const ch = tv.current
  const offAir = tv.state.offAirNumber != null
  const tag = offAir
    ? 'NO SIGNAL'
    : (ch.genre && ch.genre !== '—' ? ch.genre : ch.name).slice(0, 8).toUpperCase()

  const cls = [
    styles.remote,
    tv.state.fullscreen && styles.fullscreen,
    tv.state.fullscreen && tv.state.remoteFs === 'peek' && styles.fullscreenPeek,
    tv.state.fullscreen && tv.state.remoteFs === 'shown' && styles.fullscreenShown,
  ]
    .filter(Boolean)
    .join(' ')
  const fsLabel = tv.state.fullscreen ? 'Window' : 'Full'

  const content = (
    <>
      {/* touch only (CSS-gated): a dim scrim behind the open sheet — tap to dismiss */}
      <div className={styles.sheetScrim} onClick={() => tv.act('remote')} aria-hidden="true" />
      {/* touch only: the grabber that summons the remote sheet (shown when the sheet is closed) */}
      <button className={styles.sheetHandle} onClick={() => tv.act('remote')} aria-label="Open the remote">
        <span className={styles.sheetGrip} />
      </button>
    <div
      className={cls}
      ref={tv.registerRemote}
      data-screen-label="Remote"
      tabIndex={-1}
      style={{ ['--remote-scale' as string]: tv.state.remoteScale }}
      onPointerDown={tv.startRemoteDrag}
      onClick={tv.remoteActivate}
      onPointerMove={tv.remoteKeepAlive}
      onPointerEnter={tv.remoteKeepAlive}
    >
      <div className={styles.ir} />
      <div className={styles.head}>
        <span className={styles.brand}>ORION</span>
        <span className={styles.grip}>
          <i />
          <i />
          <i />
        </span>
      </div>
      <div className={styles.lcd}>
        <span className={styles.lcdChannel}>{!offAir && ch.slug === 'hideo' ? 'HIDEO' : `CH${pad(ch.number)}`}</span>
        <span className={styles.lcdTag}>{tag}</span>
      </div>

      <div className={styles.keys}>
        <div className={styles.buttons}>
        <div className={[styles.row, styles.two].join(' ')}>
          <button className={[styles.button, styles.power].join(' ')} data-act="power" onClick={() => tv.act('power')}>
            <span className={styles.icon}>⏻</span>Power
          </button>
          <button className={[styles.button, styles.accent].join(' ')} data-act="mute" onClick={() => tv.act('mute')}>
            <span className={styles.icon}>🔇</span>Mute
          </button>
        </div>

        <div className={styles.rockers}>
          <div className={styles.rocker}>
            <button className={styles.rockerButton} data-act="chUp" onClick={() => tv.act('chUp')}>
              ▲
            </button>
            <div className={styles.rockerLabel}>CH</div>
            <button className={styles.rockerButton} data-act="chDown" onClick={() => tv.act('chDown')}>
              ▼
            </button>
          </div>
          <div className={styles.rocker}>
            <button className={styles.rockerButton} data-act="volUp" onClick={() => tv.act('volUp')}>
              ▲
            </button>
            <div className={styles.rockerLabel}>VOL</div>
            <button className={styles.rockerButton} data-act="volDown" onClick={() => tv.act('volDown')}>
              ▼
            </button>
          </div>
        </div>

        <div className={styles.transport}>
          <button className={styles.button} data-act="progPrev" onClick={() => tv.act('progPrev')}>
            <span className={styles.icon}>⏮</span>Prev
          </button>
          <button className={styles.button} data-act="play" onClick={() => tv.act('play')}>
            <span className={styles.icon}>⏯</span>Play
          </button>
          <button className={styles.button} data-act="progNext" onClick={() => tv.act('progNext')}>
            <span className={styles.icon}>⏭</span>Next
          </button>
        </div>

        <div className={styles.row}>
          <button className={[styles.button, styles.live].join(' ')} data-act="live" onClick={() => tv.act('live')}>
            <span className={styles.icon}>●</span>Live
          </button>
        </div>

        <div className={[styles.row, styles.two].join(' ')}>
          <button className={[styles.button, styles.guide].join(' ')} data-act="guide" onClick={() => tv.act('guide')}>
            <span className={styles.icon}>☰</span>Guide
          </button>
          <button
            className={[styles.button, styles.accent].join(' ')}
            data-act="fullscreen"
            onClick={() => tv.act('fullscreen')}
          >
            <span className={styles.icon}>⛶</span>{fsLabel}
          </button>
        </div>
        <button
          className={styles.panelToggle}
          data-no-drag
          onClick={tv.remoteCollapse}
          aria-label={tv.state.remoteCollapsed ? 'Show keypad' : 'Hide keypad'}
        >
          {tv.state.remoteCollapsed ? 'SHOW KEYPAD' : 'HIDE KEYPAD'}
        </button>
        </div>

        <div className={styles.panel}>
          <Touchscreen />
        </div>
      </div>

      <div className={styles.hint}>
        Drag me anywhere. Keys: <b>P</b> power · <b>↑↓</b> channel · <b>R</b> remote
        <br />
        <b>←→</b> program · <b>+/-</b> vol · <b>G</b> guide · <b>F</b> fullscreen
      </div>
    </div>
    </>
  )

  // In TRUE (native) fullscreen the screen element is in the browser's top layer; the remote must
  // live INSIDE it to render + be summonable. Portal it there. (Windowed / in-window-fs: in place.)
  return tv.state.nativeFs && typeof document !== 'undefined' && document.fullscreenElement
    ? createPortal(content, document.fullscreenElement)
    : content
}
